import { trackError } from '@billy/track-error'

import { Organization } from '@modules-deprecated/app/organization/types/organization'

import { TargetType } from '../../enums/eInvoicing'
import { getRequest, postV2Request, putV2Request } from '../../utils'
import type {
  EInvoiceIntegrationPdpStatus,
  KYCDocument,
  KYCStatus,
  LegalUnitEstablishment,
  NetworkStatus,
  NetworkType,
} from './types'
import { DocumentType } from './types/documentType'
import { type EInvoicingAddressingLine } from './types/eInvoicingAddressingLine'

const KYC_ENDPOINT_BASE = '/e-invoicing/v1/kyc'
const E_INVOICE_INTEGRATION_ENDPOINT_BASE = '/e-invoicing/v1/e-invoice-integration'
const E_INVOICE_ENDPOINT_BASE = '/e-invoicing/v1/e-invoice'
const E_INVOICE_INTEGRATION_SIREN_ADDRESSING_LINES = '/e-invoicing/v1/pdp/directory/siren'

interface GetEInvoicingKYCStatusOptions {
  network: NetworkType
  targetId: string
  targetType?: string
}

interface GetEInvoicingKYCStatusResponse {
  data: { kycDocuments: KYCDocument[]; legalUnitEstablishment?: LegalUnitEstablishment; status: KYCStatus } | null
}

export const getEInvoicingKYCStatus = async ({
  network,
  targetId,
  targetType,
}: GetEInvoicingKYCStatusOptions): Promise<GetEInvoicingKYCStatusResponse> => {
  if (!targetId) {
    trackError('Target ID is required to get KYC status')
  }

  return getRequest(`${KYC_ENDPOINT_BASE}/${network}/${targetType}/${targetId}`)
}

interface GetEInvoicingIntegrationPdpStatusResponse {
  data: {
    isProcessing: boolean
    networkStatus?: NetworkStatus
    scope?: {
      customerInvoicesEnabled: boolean
      addressingLines?: string[]
      updatedAt: string
    }
    status: EInvoiceIntegrationPdpStatus
    statusChangedAt?: Date
  } | null
}

export const getEInvoicingIntegrationPdpStatus = async ({
  network,
  targetId,
  targetType,
}: GetEInvoicingKYCStatusOptions): Promise<GetEInvoicingIntegrationPdpStatusResponse> => {
  if (!targetId) {
    trackError('Target ID is required to get integration PDP status')
  }

  return getRequest(`${E_INVOICE_INTEGRATION_ENDPOINT_BASE}/${network}/${targetType}/${targetId}`)
}

interface EInvoicingKYCOptions {
  companyAddress: string
  companyName: string
  companyZipCode: string
  companyCity: string
  companyRegistrationNumber: string
  accountHolderFirstName?: string
  accountHolderLastName?: string
  accountHolderEmail?: string
  accountHolderCountryCode?: string
  addressingLine: string
  scope: {
    customerInvoicesEnabled: boolean
    supplierInvoicesEnabled: boolean
    selfBilledCustomerInvoicesEnabled: boolean
    selfBilledSupplierInvoicesEnabled: boolean
    eReportingEnabled: boolean
  }
  legalUnitIdentifier: string
}

export const createEInvoicingKYC =
  (organization?: Organization) =>
  async ({
    companyAddress,
    companyName,
    companyZipCode,
    companyCity,
    scope,
    legalUnitIdentifier,
    companyRegistrationNumber,
    accountHolderFirstName,
    accountHolderLastName,
    accountHolderEmail,
    accountHolderCountryCode,
    addressingLine,
  }: EInvoicingKYCOptions) => {
    if (!organization) {
      trackError('Cannot create KYC without organizationId')
      return
    }

    return postV2Request({
      endpoint: `/e-invoicing/v2/kyc/PDP/BILLY/${organization.id}`,
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      payload: {
        companyAddress,
        companyName,
        companyZipCode,
        companyCity,
        addressingLine,
        legalUnitIdentifier,
        scope,
        companyRegistrationNumber,
        accountHolderFirstName,
        accountHolderLastName,
        accountHolderEmail,
        accountHolderCountryCode,
      },
    })
  }

interface GetKYCUploadUrlOptions {
  filename: string
  network: NetworkType
  targetId: string
}

interface GetKYCUploadUrlResponse {
  data: { fileKey: string; signature: string; signedUrl: string }
}

export const getKYCUploadUrl = async ({
  filename,
  network,
  targetId,
}: GetKYCUploadUrlOptions): Promise<GetKYCUploadUrlResponse> => {
  return getRequest(`${KYC_ENDPOINT_BASE}/${network}/${TargetType.BILLY}/${targetId}/upload-url?filename=${filename}`)
}

interface UploadEInvoicingKYCFileOptions {
  signedUrl: string
  file: File
}

interface UploadEInvoicingKYCFileResponse {
  status: number
}

export const uploadEInvoicingKYCFile = async ({
  signedUrl,
  file,
}: UploadEInvoicingKYCFileOptions): Promise<UploadEInvoicingKYCFileResponse> => {
  // NOTE: Need to use the repo-standard fetch function to upload the file
  const response = await fetch(signedUrl, {
    method: 'PUT',
    body: file,
  })

  if (!response.ok) {
    const text = await response.text()
    const error = new Error('Could not upload KYC file')
    // @ts-expect-error because we're testing the errors and this wil change
    error.response = text
    // @ts-expect-error ditto
    error.headers = response.headers
    throw error
  }

  return {
    status: response.status,
  }
}

interface CreateEInvoicingKYCDocumentOptions {
  documentType: DocumentType
  fileRef: string
  mimeType: string
  network: NetworkType
  signature: string
  targetId: string
}

export const createEInvoicingKYCDocument = ({
  documentType,
  fileRef,
  mimeType,
  network,
  signature,
  targetId,
}: CreateEInvoicingKYCDocumentOptions) => {
  if (!targetId) {
    trackError('Target ID is required to create KYC document')
  }

  return postV2Request({
    endpoint: `${KYC_ENDPOINT_BASE}/document/${network}/${TargetType.BILLY}/${targetId}`,
    payload: {
      documentType,
      signature,
      fileRef,
      mimeType,
    },
  })
}

interface GetCerfaSignatureLinkOptions {
  network: NetworkType
  targetId: string
}

export interface GetCerfaSignatureLinkResponse {
  data: { signatureLink: string; signatureRequestId: string } | null
}

export const getCerfaSignatureLink = async ({
  network,
  targetId,
}: GetCerfaSignatureLinkOptions): Promise<GetCerfaSignatureLinkResponse> => {
  return getRequest(`${KYC_ENDPOINT_BASE}/cerfa-signature/${network}/${TargetType.BILLY}/${targetId}`)
}

interface ConfirmCerfaLinkOnSignOptions {
  network: NetworkType
  signatureRequestId: string
  targetId: string
}

export const confirmCerfaLinkOnSign = async ({
  network,
  signatureRequestId,
  targetId,
}: ConfirmCerfaLinkOnSignOptions): Promise<void> => {
  return postV2Request({
    endpoint: `${KYC_ENDPOINT_BASE}/cerfa-signature/${network}/${TargetType.BILLY}/${targetId}`,
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    payload: {
      signatureRequestId,
    },
  })
}

interface DisableEInvoicingOptions {
  network: NetworkType
  targetType?: string
  targetId: string
}

interface DisableEInvoicingResponse {
  data: {
    status: EInvoiceIntegrationPdpStatus
    statusChangedAt?: Date
  }
}

export const disableEInvoicing = async ({
  network,
  targetType,
  targetId,
}: DisableEInvoicingOptions): Promise<DisableEInvoicingResponse> => {
  return putV2Request({
    endpoint: `${E_INVOICE_INTEGRATION_ENDPOINT_BASE}/${network}/${targetType}/${targetId}/disable`,
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  })
}

export interface ReasonsResponseData {
  code: string
  isCommentMandatory: boolean
  title: { fr: string; en: string }
  description: { fr: string; en: string }
}

interface ReasonsResponse {
  data: {
    reasons: ReasonsResponseData[]
  } | null
}

interface GetReasonsProps {
  network: NetworkType
  targetId: string
  eInvoiceId: string
  lifecycleAction: string
  targetType: TargetType
}

export const getReasons = async ({
  network,
  targetId,
  eInvoiceId,
  lifecycleAction,
  targetType,
}: GetReasonsProps): Promise<ReasonsResponse> => {
  return getRequest(
    `${E_INVOICE_ENDPOINT_BASE}/${network}/${targetType}/${targetId}/${eInvoiceId}/reasons/${lifecycleAction}`,
  )
}
interface EInvoicingAddressingLinesResponse {
  data: EInvoicingAddressingLine[]
}

export const getEInvoicingAddressingLines = async (siren: string): Promise<EInvoicingAddressingLinesResponse> => {
  if (!siren) {
    trackError('SIREN is required to get SIREN accounts')
  }

  return getRequest(`${E_INVOICE_INTEGRATION_SIREN_ADDRESSING_LINES}/${siren}`)
}
